import React, { useState } from 'react';
import '../style.css';
import { 
  FaCar, 
  FaTaxi, 
  FaShuttleVan, 
  FaMotorcycle,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaCheckCircle,
  FaArrowRight,
  FaStar
} from 'react-icons/fa';
import { 
  MdAirportShuttle,
  MdSafetyDivider
} from 'react-icons/md';
import { 
  GiCommercialAirplane
} from 'react-icons/gi';

const ServiceSection = () => {
  const [activeCard, setActiveCard] = useState(null);

  const services = [
    {
      id: 1,
      icon: <FaCar className="rotating-icon" />,
      title: "City Rides",
      description: "Affordable and comfortable rides within the city.",
      features: ["24/7 Available", "Multiple Stops", "Live Tracking"],
      price: "₹15/km",
      color: "#3b82f6",
      hoverColor: "#2563eb",
      delay: "0.1s"
    },
    {
      id: 2,
      icon: <FaTaxi className="rotating-icon" />,
      title: "Premium Taxi",
      description: "Luxury taxis for business or special occasions.",
      features: ["Premium Cars", "Professional Drivers", "WiFi Available"],
      price: "₹25/km",
      color: "#f97316",
      hoverColor: "#ea580c",
      delay: "0.2s"
    },
    {
      id: 3,
      icon: <MdAirportShuttle className="rotating-icon" />,
      title: "Airport Transfer",
      description: "Reliable airport transfers with flight tracking.",
      features: ["Flight Tracking", "Meet & Greet", "30min Waiting"],
      price: "Fixed Rates",
      color: "#0ea5e9",
      hoverColor: "#0284c7",
      delay: "0.3s"
    },
    {
      id: 4,
      icon: <FaShuttleVan className="rotating-icon" />,
      title: "Outstation Trips",
      description: "Comfortable long-distance trips with experienced drivers.",
      features: ["Round Trips", "Multiple Options", "Hotel Pickup"],
      price: "₹12/km",
      color: "#f59e0b", // Changed from green to amber (orange shade)
      hoverColor: "#d97706",
      delay: "0.4s"
    },
    {
      id: 5,
      icon: <FaMotorcycle className="rotating-icon" />,
      title: "Bike Taxi",
      description: "Quick and economical rides through traffic.",
      features: ["Beat Traffic", "Economical", "Quick Pickup"],
      price: "₹5/km",
      color: "#1d4ed8",
      hoverColor: "#1e40af",
      delay: "0.5s"
    },
    {
      id: 6,
      icon: <GiCommercialAirplane className="rotating-icon" />,
      title: "Corporate Travel",
      description: "Dedicated corporate travel solutions.",
      features: ["Monthly Billing", "Dedicated Fleet", "Travel Reports"],
      price: "Custom Plans",
      color: "#1e40af",
      hoverColor: "#1e3a8a",
      delay: "0.6s"
    }
  ];

  return (
    <section className="service-section">
      {/* Floating Background Elements */}
      <div className="floating-bg">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="floating-element" style={{ '--i': i }}></div>
        ))}
      </div>
      
      <div className="container">
        {/* Section Header */}
        <div className="section-header">
          <div className="title-wrapper">
            <h2 className="section-title">Our Premium Services</h2>
            <div className="title-underline">
              <div className="underline-animation"></div>
            </div>
          </div>
          <p className="section-description">
            Experience reliable, comfortable, and safe cab services for all your travel needs
          </p>
        </div>

        {/* Service Cards */}
        <div className="services-grid">
          {services.map((service) => (
            <div 
              key={service.id}
              className={`service-card ${activeCard === service.id ? 'active' : ''}`}
              onMouseEnter={() => setActiveCard(service.id)}
              onMouseLeave={() => setActiveCard(null)}
              style={{ 
                animationDelay: service.delay,
                '--card-color': service.color,
                '--card-hover-color': service.hoverColor
              }}
            >
              {/* Card Glow Effects */}
              <div className="card-glow-1" style={{ background: service.color }}></div>
              <div className="card-glow-2" style={{ background: service.color }}></div>
              
              {/* Card Badge */}
              <div className="card-badge" style={{ background: service.color }}>
                <FaStar className="badge-icon" />
                <span>Popular</span>
              </div>

              {/* Card Icon with Animation */}
              <div className="card-icon-container">
                <div 
                  className="card-icon-ring"
                  style={{ 
                    borderColor: service.color,
                    boxShadow: `0 0 20px ${service.color}40`
                  }}
                >
                  <div 
                    className="card-icon"
                    style={{ 
                      background: `linear-gradient(135deg, ${service.color}, ${service.hoverColor})`
                    }}
                  >
                    {service.icon}
                  </div>
                  <div className="icon-pulse" style={{ borderColor: service.color }}></div>
                </div>
              </div>

              {/* Card Content */}
              <div className="card-content">
                <div className="card-header">
                  <h3 className="service-title" style={{ color: service.color }}>
                    {service.title}
                  </h3>
                  <div className="service-rating">
                    <FaStar className="star-icon" />
                    <span>4.8</span>
                  </div>
                </div>
                
                <p className="service-description">{service.description}</p>
                
                <div className="service-features">
                  {service.features.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <FaCheckCircle className="check-icon" style={{ color: service.color }} />
                      <span>{feature}</span>
                      <div className="feature-line" style={{ background: service.color }}></div>
                    </div>
                  ))}
                </div>

                <div className="price-section">
                  <div className="price-tag" style={{ background: service.color }}>
                    <span className="price-text">{service.price}</span>
                    <div className="price-shine"></div>
                  </div>
                </div>

                {/* Animated Button */}
                <button 
                  className="book-btn" 
                  style={{ 
                    background: `linear-gradient(135deg, ${service.color} 0%, ${service.hoverColor} 100%)`
                  }}
                >
                  <span className="btn-text">Book Now</span>
                  <div className="btn-arrow-wrapper">
                    <FaArrowRight className="btn-arrow" />
                  </div>
                  <div className="btn-shine"></div>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Info Cards */}
        <div className="bottom-info">
          <div className="info-card">
            <div className="info-icon-container">
              <div className="info-icon" style={{ background: '#3b82f6' }}>
                <FaPhoneAlt />
              </div>
              <div className="info-icon-orbital">
                <div className="orbital-dot" style={{ background: '#3b82f6' }}></div>
                <div className="orbital-dot" style={{ background: '#3b82f6' }}></div>
                <div className="orbital-dot" style={{ background: '#3b82f6' }}></div>
              </div>
            </div>
            <div className="info-content">
              <h4>24/7 Support</h4>
              <p>Call us anytime for assistance</p>
              <span className="phone-number">
                <FaPhoneAlt className="phone-icon" />
                6307291567
              </span>
            </div>
          </div>
          
          <div className="info-card">
            <div className="info-icon-container">
              <div className="info-icon" style={{ background: '#f97316' }}> {/* Changed to orange */}
                <MdSafetyDivider />
              </div>
              <div className="info-icon-orbital">
                <div className="orbital-dot" style={{ background: '#f97316' }}></div>
                <div className="orbital-dot" style={{ background: '#f97316' }}></div>
                <div className="orbital-dot" style={{ background: '#f97316' }}></div>
              </div>
            </div>
            <div className="info-content">
              <h4>Safety First</h4>
              <p>Verified drivers and secure rides</p>
              <div className="safety-tags">
                <span className="tag">Live Tracking</span>
                <span className="tag">SOS Button</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceSection;